// src/components/OrgAssignAssessModal.js
import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useApi } from "../hooks/useApi";
import { useAuth } from "../contexts/AuthContext";
import { usePrivateDealAssignment } from "../hooks/usePrivateDealAssignment";
import UserSelect from "./UserSelect";

const OrgAssignAssessModal = ({
    isOpen,
    onClose,
    organizationId,
    vendorId,            // Required for TPRM assessments
    assessmentId,        // Optional: if provided, use directly (skip lookup)
    assessmentType,      // "enterprise" | "ransomware" | "darkweb" | "tprm" | "ai"
    hasExistingAssessment,
    refreshData,
    dealId,              // Optional: deal context when reassigning a deal-scoped assessment (e.g. from My Tasks)
}) => {
    const [users, setUsers] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(true);
    const [selectedUserId, setSelectedUserId] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const { user } = useAuth();
    const apiCall = useApi();

    // Private-deal assignment rules (inert when no dealId / deal is public). When the
    // assessment is linked to a private deal, non-owner/admin assigners only see deal
    // members; the deal's owner/admin sees everyone (non-members tagged) and can grant
    // Contributor access on the fly.
    const { isPrivateDeal, memberIds, canManagePrivateDeal, grantDealContributor } =
        usePrivateDealAssignment(isOpen, dealId);

    // Assessment type configurations for creating and assigning assessments
    const ASSESSMENT_CONFIG = {
        standard: {
            checkEndpoint: (orgId) => `/assessments?organizationId=${orgId}`,
            assignableUsersEndpoint: "/assessments/assignableUsers",
            idField: "assessmentID",
            assignEndpoint: (assessmentId) => `/assessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                assignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                assignedToUserInviteId: inviteId,
            }),
        },
        businessUnit: {
            checkEndpoint: (orgId) => `/businessUnitAssessments?organizationId=${orgId}`,
            assignableUsersEndpoint: "/businessUnitAssessments/assignableUsers",
            idField: "businessUnitAssessmentId",
            assignEndpoint: (assessmentId) => `/businessUnitAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                assignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                assignedToUserInviteId: inviteId,
            }),
        },
        enterprise: {
            checkEndpoint: (orgId) => `/enterpriseAssessments?organizationId=${orgId}`,
            createEndpoint: "/enterpriseAssessments",
            assignableUsersEndpoint: "/enterpriseAssessments/assignableUsers",
            buildPayload: (orgId) => ({
                DealId: null,
                OrganizationId: orgId,
                enterpriseAssessmentType: "Initial",
                enterpriseFramework: "Default",
                enterpriseAssessmentDate: new Date().toISOString(),
                enterpriseStatus: "in_progress",
                ApplicationTypeId: null,
                RegionId: null,
                VerticalId: null,
                SubVerticalId: null,
                DeploymentModelId: null,
                UserBaseId: null
            }),
            idField: "enterpriseAssessmentID",
            assignEndpoint: (assessmentId) => `/enterpriseAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                enterpriseAssignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                enterpriseAssignedToUserInviteId: inviteId,
            }),
        },
        ransomware: {
            checkEndpoint: (orgId) => `/ransomwareAssessments?organizationId=${orgId}`,
            createEndpoint: "/ransomwareAssessments",
            assignableUsersEndpoint: "/ransomwareAssessments/assignableUsers",
            buildPayload: (orgId) => ({
                DealId: null,
                OrganizationId: orgId,
                RansomwareAssessmentType: "Initial",
                RansomwareFramework: "Default",
                RansomwareAssessmentDate: new Date().toISOString(),
                RansomwareStatus: "in_progress",
                ApplicationTypeId: null,
                RegionId: null,
                VerticalId: null,
                SubVerticalId: null,
                DeploymentModelId: null,
                UserBaseId: null
            }),
            idField: "ransomwareAssessmentID",
            assignEndpoint: (assessmentId) => `/ransomwareAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                ransomwareAssignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                ransomwareAssignedToUserInviteId: inviteId,
            }),
        },
        darkweb: {
            checkEndpoint: (orgId) => `/darkWebAssessments?organizationId=${orgId}`,
            createEndpoint: "/darkWebAssessments",
            assignableUsersEndpoint: "/darkWebAssessments/assignableUsers",
            buildPayload: (orgId) => ({
                DealId: null,
                OrganizationId: orgId,
                darkWebAssessmentType: "Initial",
                darkWebFramework: "Default",
                darkWebAssessmentDate: new Date().toISOString(),
                darkWebStatus: "in_progress",
                ApplicationTypeId: null,
                RegionId: null,
                VerticalId: null,
                SubVerticalId: null,
                DeploymentModelId: null,
                UserBaseId: null
            }),
            idField: "darkWebAssessmentID",
            assignEndpoint: (assessmentId) => `/darkWebAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                darkWebAssignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                darkWebAssignedToUserInviteId: inviteId,
            }),
        },
        tprm: {
            checkEndpoint: (orgId, vendorId) => `/TPRMAssessments/vendor/?vendorId=${vendorId}`,
            createEndpoint: "/TPRMAssessments",
            assignableUsersEndpoint: "/TPRMAssessments/assignableUsers",
            buildPayload: (orgId, vendorId) => ({
                DealId: null,
                OrganizationId: orgId,
                VendorId: vendorId,
                TPRMAssessmentType: "Initial",
                TPRMFramework: "Default",
                TPRMAssessmentDate: new Date().toISOString(),
                TPRMStatus: "in_progress",
                TPRMProgress: 0,
                RegionId: null,
                VerticalId: null,
                SubVerticalId: null,
            }),
            idField: "tprmAssessmentID",
            assignEndpoint: (assessmentId) => `/TPRMAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                tprmAssignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                tprmAssignedToUserInviteId: inviteId,
            }),
        },
        ai: {
            checkEndpoint: (orgId) => `/aiAssessments?organizationId=${orgId}`,
            createEndpoint: "/aiAssessments",
            assignableUsersEndpoint: "/aiAssessments/assignableUsers",
            buildPayload: (orgId) => ({
                DealId: null,
                OrganizationId: orgId,
                AIAssessmentType: "Initial",
                AIFramework: "Default",
                AIAssessmentDate: new Date().toISOString(),
                AIStatus: "pending",
            }),
            idField: "aiAssessmentID",
            assignEndpoint: (assessmentId) => `/aiAssessments/${assessmentId}/assign`,
            buildAssignPayload: (toUserId, byUserId) => ({
                AIAssignedToUserId: toUserId,
            }),
            buildAssignInvitePayload: (inviteId, byUserId) => ({
                AIAssignedToUserInviteId: inviteId,
            }),
        }
    };

    // Get or create assessment for this organization, returns assessment ID
    const ensureAssessmentExists = async () => {
        const config = ASSESSMENT_CONFIG[assessmentType];
        if (!config) {
            throw new Error(`Unknown assessment type: ${assessmentType}`);
        }

        try {
            // Fetch assessments for this organization/vendor (need to do this even if hasExistingAssessment to get the ID)
            const existingAssessments = await apiCall("GET", config.checkEndpoint(organizationId, vendorId));

            if (existingAssessments && existingAssessments.length > 0) {
                // Handle both uppercase ID (GET response) and lowercase Id (POST response)
                const existingId = existingAssessments[0]?.[config.idField]
                    || existingAssessments[0]?.[config.idField.replace(/ID$/, 'Id')]
                    || existingAssessments[0]?.id;
                return existingId;
            }

            const payload = config.buildPayload(organizationId, vendorId);
            const created = await apiCall("POST", config.createEndpoint, payload);

            // Handle both uppercase ID (GET response) and lowercase Id (POST response)
            const assessmentId = created?.[config.idField]
                || created?.[config.idField.replace(/ID$/, 'Id')]
                || created?.id
                || created?.assessmentID;
            if (!assessmentId) {
                throw new Error("Assessment creation failed: no ID returned");
            }

            return assessmentId;
        } catch (err) {
            throw err;
        }
    };

    // Fetch users when modal opens
    useEffect(() => {
        if (isOpen) fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    const fetchUsers = async () => {
        try {
            // Use the assignableUsers endpoint to get users with Contributor+ role
            const config = ASSESSMENT_CONFIG[assessmentType];
            const endpoint = config?.assignableUsersEndpoint || "/users";
            const res = await apiCall("GET", endpoint);
            setUsers(res || []);
        } catch (err) {} finally {
            setLoadingUsers(false);
        }
    };

    // Called after a successful invite — no immediate PUT, just let UserSelect handle it
    const handleInviteSuccess = () => {
        // No-op: the invited user is added to the dropdown and auto-selected by UserSelect.
        // The actual assignment happens when the user clicks Save.
    };

    // Handle save assignment
    const handleSave = async (e) => {
        e.preventDefault();

        if (!selectedUserId) {
            alert("Please select a user to assign.");
            return;
        }

        setIsProcessing(true);

        try {
            const config = ASSESSMENT_CONFIG[assessmentType];

            // Private deal + owner/admin assigning a non-member: confirm, then grant
            // CONTRIBUTOR@DEAL before the assignment (the backend rejects non-members).
            const isNonMemberPick = !selectedUserId.startsWith("invite:")
                && isPrivateDeal && canManagePrivateDeal && memberIds
                && !memberIds.has(parseInt(selectedUserId));
            if (isNonMemberPick) {
                const selected = users.find(u => String(u.id) === String(selectedUserId));
                const assigneeName = selected?.fullName || selected?.email || `User #${selectedUserId}`;
                const confirmed = window.confirm(
                    `${assigneeName} can't see this private deal. Grant Contributor access and assign?`
                );
                if (!confirmed) {
                    setIsProcessing(false);
                    return;
                }
                await grantDealContributor(parseInt(selectedUserId));
            }

            // Step 1: Get or create assessment, returns assessment ID
            // If assessmentId prop is provided, use it directly (skip lookup)
            const resolvedAssessmentId = assessmentId || (await ensureAssessmentExists());

            // Step 2: Build type-specific assign payload (invite vs regular user)
            const isInvite = selectedUserId.startsWith("invite:");
            const payload = isInvite
                ? config.buildAssignInvitePayload(
                      parseInt(selectedUserId.split(":")[1]),
                      user?.userId
                  )
                : config.buildAssignPayload(
                      parseInt(selectedUserId),
                      user?.userId
                  );

            // Due date is only supported by the TPRM assign endpoint (defaults to +30 days server-side)
            if (assessmentType === "tprm" && dueDate) {
                payload.dueDate = dueDate;
            }

            // Step 3: Call assessment-specific assign endpoint
            await apiCall("PUT", config.assignEndpoint(resolvedAssessmentId), payload);
            refreshData?.();
            onClose();
        } catch (err) {
            alert("Failed to assign assessment. Check console for details.");
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border max-w-lg w-full shadow-lg rounded-md bg-white">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-content-primary">
                        Assign Assessment
                    </h3>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600"
                    >
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <form onSubmit={handleSave} className="space-y-4">
                    {/* User Dropdown */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Assign To
                        </label>
                        <UserSelect
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            users={(isPrivateDeal && !canManagePrivateDeal && memberIds)
                                ? users.filter(u => memberIds.has(u.id))
                                : users}
                            loading={loadingUsers}
                            canInvite={!isPrivateDeal || canManagePrivateDeal}
                            nonMemberIds={(isPrivateDeal && canManagePrivateDeal && memberIds)
                                ? new Set(users.filter(u => !memberIds.has(u.id)).map(u => u.id))
                                : undefined}
                            onInviteSuccess={handleInviteSuccess}
                        />
                        {isPrivateDeal && !canManagePrivateDeal && (
                            <p className="text-xs text-gray-500 mt-1">
                                Only the deal owner can invite external users to a private deal.
                            </p>
                        )}
                        {isPrivateDeal && canManagePrivateDeal && (
                            <p className="text-xs text-gray-500 mt-1">
                                Invited users will be granted access to this private deal when they join.
                            </p>
                        )}
                    </div>

                    {/* Due date (TPRM only — server defaults to +30 days when omitted) */}
                    {assessmentType === "tprm" && (
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Due Date <span className="text-gray-400 font-normal">(optional, defaults to 30 days)</span>
                            </label>
                            <input
                                type="date"
                                value={dueDate}
                                onChange={(e) => setDueDate(e.target.value)}
                                min={new Date().toISOString().split("T")[0]}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex justify-end space-x-3 mt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={isProcessing || loadingUsers || !selectedUserId}
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isProcessing ? "Processing..." : "Save"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default OrgAssignAssessModal;
